import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { format, parseISO, addDays, isSameMonth, isFriday, isAfter, startOfDay, startOfWeek } from 'date-fns';
import {
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Calendar,
  TrendingUp,
  Filter,
  RefreshCw,
  MessageSquare,
  ClipboardList,
  ExternalLink,
  Lock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Approvals = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // Current week data
  const [currentWeek, setCurrentWeek] = useState(null);
  
  // History data
  const [history, setHistory] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [weekBreakdowns, setWeekBreakdowns] = useState({});
  
  // Stats
  const [stats, setStats] = useState({ total_submitted: 0, approved: 0, rejected: 0, pending: 0 });
  
  // Deadline
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  
  // UI state
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitNote, setSubmitNote] = useState('');
  const [noteExpanded, setNoteExpanded] = useState(false);
  const NOTE_MAX_LENGTH = 200;

  useEffect(() => {
    fetchAllData();
  }, [api]);

  useEffect(() => {
    fetchHistory();
  }, [statusFilter, periodFilter, api]);

  const fetchAllData = async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [currentRes, statsRes, deadlineRes] = await Promise.all([
        api.get('/approvals/current-week'),
        api.get('/approvals/stats'),
        api.get('/approvals/deadline-check')
      ]);
      setCurrentWeek(currentRes.data);
      setStats(statsRes.data);
      setDeadlineInfo(deadlineRes.data);
    } catch (error) {
      console.error('Failed to fetch approval data:', error);
      toast.error('Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!api) return;
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (periodFilter !== 'all') params.append('period', periodFilter);
      
      const res = await api.get(`/approvals/history?${params.toString()}`);
      setHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const fetchWeekBreakdown = async (weekStart) => {
    if (weekBreakdowns[weekStart]) return;
    try {
      const res = await api.get(`/approvals/week/${weekStart}/breakdown`);
      setWeekBreakdowns(prev => ({ ...prev, [weekStart]: res.data }));
    } catch (error) {
      console.error('Failed to fetch breakdown:', error);
    }
  };

  const handleSubmit = async () => {
    if (!currentWeek?.timesheet?.id) return;
    setSubmitting(true);
    try {
      await api.post(`/timesheets/${currentWeek.timesheet.id}/submit`, {
        note: submitNote || undefined
      });
      toast.success('Timesheet submitted for approval');
      setShowSubmitDialog(false);
      setSubmitNote('');
      fetchAllData();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubmit = async () => {
    if (!currentWeek?.timesheet?.id) return;
    setSubmitting(true);
    try {
      await api.post(`/timesheets/${currentWeek.timesheet.id}/submit`, {
        note: submitNote || undefined
      });
      toast.success('Timesheet resubmitted for approval');
      setSubmitNote('');
      fetchAllData();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = async (weekStart) => {
    try {
      const res = await api.get(`/timesheets/week/${weekStart}`);
      const timesheet = res.data.timesheet;
      if (!timesheet?.id) return;
      
      const exportRes = await api.get(`/timesheets/${timesheet.id}/export?format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([exportRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet-${weekStart}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Timesheet exported');
    } catch (error) {
      toast.error('Failed to export timesheet');
    }
  };

  const toggleWeekExpand = (weekStart) => {
    if (!expandedWeeks[weekStart]) {
      fetchWeekBreakdown(weekStart);
    }
    setExpandedWeeks(prev => ({ ...prev, [weekStart]: !prev[weekStart] }));
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { icon: ClipboardList, label: 'Draft', class: 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-700 dark:text-slate-300', cardClass: 'border-slate-200 dark:border-slate-700' },
      submitted: { icon: Lock, label: t('timesheet.pendingReview'), class: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300', cardClass: 'border-l-4 border-l-blue-500 border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-900/30/50 dark:bg-blue-900/5' },
      approved: { icon: CheckCircle2, label: t('approvals.approved'), class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300', cardClass: 'border-emerald-200 dark:border-emerald-500/30' },
      rejected: { icon: XCircle, label: t('approvals.rejected'), class: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300', cardClass: 'border-red-200 dark:border-red-500/30' }
    };
    return configs[status] || configs.draft;
  };

  // Get due date info for draft timesheets
  const getDueDateInfo = (weekStart) => {
    if (!weekStart) return { text: '', isUrgent: false };
    const start = parseISO(weekStart);
    const friday = addDays(start, 4); // Friday of the week
    const today = startOfDay(new Date());
    
    const isToday = format(today, 'yyyy-MM-dd') === format(friday, 'yyyy-MM-dd');
    const isPast = isAfter(today, friday);
    const isTodayOrPast = isToday || isPast;
    
    if (isToday) {
      return { text: 'Due today', isUrgent: true };
    } else if (isPast) {
      return { text: 'Overdue', isUrgent: true };
    } else {
      return { text: `Due ${format(friday, 'EEEE, MMM d')}`, isUrgent: false };
    }
  };

  // Get project count from current week
  const projectCount = currentWeek?.project_breakdown?.length || 0;

  // Fixed date format - always include month name for end date when it differs
  const formatWeekRange = (weekStart) => {
    if (!weekStart) return '';
    const start = parseISO(weekStart);
    const end = addDays(start, 6);
    
    // Check if months are different
    if (!isSameMonth(start, end)) {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    }
    return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`;
  };

  // Fixed time format - always "Xh Xm"
  const formatHours = (h) => {
    if (!h || h === 0) return '0h 0m';
    const hours = Math.floor(h);
    const minutes = Math.round((h - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  // Check if all stats are zero
  const allStatsZero = stats.total_submitted === 0 && stats.approved === 0 && stats.rejected === 0 && stats.pending === 0;

  // Filter history to exclude draft weeks (only show submitted, approved, rejected)
  const filteredHistory = useMemo(() => {
    return history.filter(ts => ts.status !== 'draft');
  }, [history]);

  // Check if current week submission is restricted (Mon-Thu only)
  const submissionRestriction = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    if (dayOfWeek >= 1 && dayOfWeek <= 4) {
      const daysUntilFriday = 5 - dayOfWeek;
      return {
        restricted: true,
        reason: `Current week's timesheet can only be submitted on Friday, Saturday, or Sunday. ${daysUntilFriday} day${daysUntilFriday > 1 ? 's' : ''} until Friday.`
      };
    }
    return { restricted: false, reason: '' };
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  const status = currentWeek?.timesheet?.status || 'draft';
  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig.icon;

  return (
    <Layout>
      <div className="space-y-6" data-testid="approvals-page">
        {/* Deadline Reminder Banner */}
        {deadlineInfo?.show_reminder && (
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4" data-testid="deadline-banner">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  {deadlineInfo.is_past_deadline 
                    ? 'Your timesheet for this week is overdue!' 
                    : 'Your timesheet for this week is due today'}
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  Please submit before the deadline to ensure timely approval.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setShowSubmitDialog(true)}
                className="btn-premium h-9"
              >
                Submit Now
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('approvals.title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('approvals.subtitle')}</p>
          </div>
        </div>

        {/* Submission Restriction Banner */}
        {submissionRestriction.restricted && (
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4" data-testid="submission-restricted-banner">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  {t('approvals.submissionNotAvailable')}
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  {submissionRestriction.reason}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Row - Meaningful pills */}
        <div className="flex flex-wrap gap-3" data-testid="stats-row">
          {allStatsZero ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-full">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">
                {t('approvals.noSubmissions')}
              </span>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/20 rounded-full">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  {stats.approved} approved
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <span className="text-sm font-medium text-red-700 dark:text-red-300">
                  {stats.rejected} rejected
                </span>
              </div>
              {stats.pending > 0 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                  <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    {stats.pending} pending
                  </span>
                </div>
              )}
              <span className="text-xs text-slate-400 self-center ml-1">This month</span>
            </>
          )}
        </div>

        {/* Current Week Card - Expanded with project breakdown */}
        <div className={`premium-card ${status === 'submitted' ? '' : 'border-2'} ${statusConfig.cardClass}`} data-testid="current-week-card">
          <div className="p-5">
            {/* Header row */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${statusConfig.class}`}>
                  <StatusIcon className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">This Week</h2>
                    {/* Improved Draft badge with due date */}
                    {status === 'draft' ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                        getDueDateInfo(currentWeek?.timesheet?.week_start).isUrgent
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        <ClipboardList className="w-3 h-3" />
                        Draft · {getDueDateInfo(currentWeek?.timesheet?.week_start).text}
                      </span>
                    ) : status === 'submitted' ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${statusConfig.class}`}>
                        <Lock className="w-3 h-3" />
                        Pending Review
                      </span>
                    ) : (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.class}`}>
                        {statusConfig.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">
                    {currentWeek?.timesheet ? formatWeekRange(currentWeek.timesheet.week_start) : ''}
                  </p>
                </div>
              </div>
              
              {/* Total hours - with label */}
              <div className="text-right">
                <p className="text-xs text-slate-400 mb-0.5">Total this week</p>
                <span className="text-3xl font-bold text-slate-900 dark:text-white">
                  {formatHours(currentWeek?.timesheet?.total_hours)}
                </span>
              </div>
            </div>

            {/* Project breakdown */}
            {currentWeek?.project_breakdown?.length > 0 && (
              <div className="space-y-2 mb-3">
                {currentWeek.project_breakdown.map(project => (
                  <div 
                    key={project.id} 
                    className="flex items-center gap-2 text-sm"
                  >
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: project.color }}
                    />
                    <span className="text-slate-600 dark:text-slate-400">{project.name}</span>
                    <span className="text-slate-400 dark:text-slate-500">—</span>
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {formatHours(project.hours)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Locked message for submitted status */}
            {status === 'submitted' && (
              <p className="text-xs text-slate-500 italic mb-3">
                Submitted to your manager for review. You cannot edit this timesheet until it is reviewed.
              </p>
            )}

            {/* View Full Timesheet link */}
            {(status === 'draft' || status === 'rejected') && (
              <Link 
                to="/timesheet" 
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 mb-3"
              >
                View detailed timesheet <ExternalLink className="w-3 h-3" />
              </Link>
            )}

            {/* Divider */}
            <div className="border-t border-slate-200 dark:border-slate-700 my-4" />

            {/* Status-specific content */}
            {status === 'draft' && (
              <div className="space-y-3">
                {/* Collapsible Note field */}
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">
                    Add a note for your manager (optional)
                  </label>
                  {noteExpanded ? (
                    <div className="relative">
                      <Textarea
                        placeholder="e.g., Worked overtime on urgent project"
                        value={submitNote}
                        onChange={(e) => setSubmitNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                        onBlur={() => !submitNote && setNoteExpanded(false)}
                        rows={3}
                        className="resize-none"
                        autoFocus
                      />
                      <span className="absolute bottom-2 right-2 text-[10px] text-slate-400">
                        {submitNote.length}/{NOTE_MAX_LENGTH}
                      </span>
                    </div>
                  ) : (
                    <Input
                      placeholder="e.g., Worked overtime on urgent project"
                      value={submitNote}
                      onChange={(e) => setSubmitNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                      onFocus={() => setNoteExpanded(true)}
                      className="h-10"
                    />
                  )}
                </div>
                {/* Footer with link and button */}
                <div className="flex items-center justify-between pt-1">
                  <Link 
                    to="/timesheet" 
                    className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    View Full Timesheet →
                  </Link>
                  <Button
                    onClick={() => setShowConfirmDialog(true)}
                    className="btn-premium h-10 px-6"
                    disabled={submissionRestriction.restricted}
                    data-testid="submit-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Submit for Approval
                  </Button>
                </div>
              </div>
            )}

            {status === 'rejected' && (
              <div className="space-y-3">
                <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-700 dark:text-red-300">
                      Rejected
                    </span>
                  </div>
                  {currentWeek?.timesheet?.rejection_note && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {currentWeek.timesheet.rejection_note}
                    </p>
                  )}
                </div>
                {/* Collapsible Note field for resubmission */}
                <div>
                  <label className="text-xs text-slate-500 mb-1.5 block">
                    Add a note for your manager (optional)
                  </label>
                  {noteExpanded ? (
                    <div className="relative">
                      <Textarea
                        placeholder="e.g., Made corrections as requested"
                        value={submitNote}
                        onChange={(e) => setSubmitNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                        onBlur={() => !submitNote && setNoteExpanded(false)}
                        rows={3}
                        className="resize-none"
                        autoFocus
                      />
                      <span className="absolute bottom-2 right-2 text-[10px] text-slate-400">
                        {submitNote.length}/{NOTE_MAX_LENGTH}
                      </span>
                    </div>
                  ) : (
                    <Input
                      placeholder="e.g., Made corrections as requested"
                      value={submitNote}
                      onChange={(e) => setSubmitNote(e.target.value.slice(0, NOTE_MAX_LENGTH))}
                      onFocus={() => setNoteExpanded(true)}
                      className="h-10"
                    />
                  )}
                </div>
                {/* Footer with link and button */}
                <div className="flex items-center justify-between pt-1">
                  <Link 
                    to="/timesheet" 
                    className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    View Full Timesheet →
                  </Link>
                  <Button
                    onClick={handleResubmit}
                    disabled={submitting || submissionRestriction.restricted}
                    className="btn-premium h-10 px-6"
                  >
                    {submitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Resubmitting...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Revise & Resubmit
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timesheet History Section (renamed from Approval History) */}
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Timesheet History
            </h2>
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 h-10 rounded-xl" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="submitted">Pending</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={periodFilter} onValueChange={setPeriodFilter}>
                <SelectTrigger className="w-40 h-10 rounded-xl" data-testid="period-filter">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="3months">Last 3 Months</SelectItem>
                  <SelectItem value="year">This Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* History List - filtered to exclude drafts */}
          <div className="space-y-2" data-testid="history-list">
            {filteredHistory.length === 0 ? (
              <div className="premium-card p-8 text-center">
                <FileText className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Your submitted timesheets will appear here</p>
              </div>
            ) : (
              filteredHistory.map((ts) => {
                const tsStatus = getStatusConfig(ts.status);
                const TsIcon = tsStatus.icon;
                const isExpanded = expandedWeeks[ts.week_start];
                const breakdown = weekBreakdowns[ts.week_start];
                
                // Count projects
                const projectCount = breakdown?.project_breakdown?.length || 0;
                
                return (
                  <Collapsible 
                    key={ts.week_start} 
                    open={isExpanded} 
                    onOpenChange={() => toggleWeekExpand(ts.week_start)}
                  >
                    <div className="premium-card overflow-hidden">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center gap-4">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                          <div className="text-left">
                            <p className="text-sm font-medium text-slate-900 dark:text-white">
                              {formatWeekRange(ts.week_start)}
                            </p>
                            {/* Approval/rejection info */}
                            {ts.status === 'approved' && ts.approved_by_name && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Approved by {ts.approved_by_name}
                              </p>
                            )}
                            {ts.status === 'rejected' && ts.rejection_note && (
                              <p className="text-xs text-red-500 mt-0.5 line-clamp-1">
                                Rejected — {ts.rejection_note}
                              </p>
                            )}
                            {ts.status === 'submitted' && ts.submitted_at && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Submitted {format(parseISO(ts.submitted_at), 'MMM d, yyyy')}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">
                              {formatHours(ts.total_hours)}
                            </span>
                            {projectCount > 0 && (
                              <p className="text-xs text-slate-500">
                                {projectCount} project{projectCount !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tsStatus.class}`}>
                            <TsIcon className="w-3 h-3" />
                            {tsStatus.label}
                          </span>
                          {ts.status === 'rejected' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/timesheet');
                              }}
                              className="h-8 text-xs text-blue-600 hover:text-blue-700"
                            >
                              Revise & Resubmit →
                            </Button>
                          )}
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-slate-800">
                          <div className="pl-8 mt-4">
                            {breakdown ? (
                              <>
                                {/* Project breakdown */}
                                {breakdown.project_breakdown?.length > 0 ? (
                                  <div className="space-y-2 mb-4">
                                    {breakdown.project_breakdown.map(project => (
                                      <div 
                                        key={project.id}
                                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
                                      >
                                        <div className="flex items-center gap-2">
                                          <div 
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: project.color }}
                                          />
                                          <span className="text-sm text-slate-700 dark:text-slate-300">
                                            {project.name}
                                          </span>
                                        </div>
                                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                                          {formatHours(project.hours)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-500 py-2 italic mb-4">No time entries for this week</p>
                                )}
                                
                                {/* Manager's comment or rejection note */}
                                {ts.status === 'rejected' && ts.rejection_note && (
                                  <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                      <MessageSquare className="w-3.5 h-3.5 text-red-500" />
                                      <span className="text-xs font-medium text-red-600 dark:text-red-400">Manager's Note</span>
                                    </div>
                                    <p className="text-sm text-red-600 dark:text-red-400">{ts.rejection_note}</p>
                                  </div>
                                )}

                                {/* Dates and export */}
                                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
                                  <div className="text-xs text-slate-500 space-y-0.5">
                                    {ts.submitted_at && (
                                      <p>Submitted: {format(parseISO(ts.submitted_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
                                    )}
                                    {ts.status === 'approved' && ts.approved_at && (
                                      <p>Approved: {format(parseISO(ts.approved_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
                                    )}
                                    {ts.status === 'rejected' && ts.rejected_at && (
                                      <p>Rejected: {format(parseISO(ts.rejected_at), 'MMM d, yyyy \'at\' h:mm a')}</p>
                                    )}
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleExportCSV(ts.week_start)}
                                    className="h-8 text-xs"
                                  >
                                    <Download className="w-3.5 h-3.5 mr-1.5" />
                                    Export
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center gap-2 py-2">
                                <div className="w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                                <p className="text-sm text-slate-500">Loading breakdown...</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog (shows before actual submission) */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Confirm Submission</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit this timesheet for approval?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Week Summary */}
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <span className="text-sm text-slate-500">Week</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">
                {currentWeek?.timesheet ? formatWeekRange(currentWeek.timesheet.week_start) : ''}
              </span>
            </div>

            {/* Total Hours */}
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-500/20">
              <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">Total Hours</span>
              <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
                {formatHours(currentWeek?.timesheet?.total_hours)}
              </span>
            </div>

            {/* Warning message */}
            <p className="text-xs text-slate-500 text-center">
              Once submitted, you won't be able to edit this timesheet until it's reviewed by your manager.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} className="rounded-xl h-10">
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowConfirmDialog(false);
                handleSubmit();
              }} 
              disabled={submitting}
              className="btn-premium h-10 px-6"
              data-testid="confirm-submit-btn"
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Yes, Submit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Dialog (for deadline reminder) */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="submit-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Submit Timesheet</DialogTitle>
            <DialogDescription>
              Review your hours before submitting for approval
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Week */}
            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <span className="text-sm text-slate-500">Week</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">
                {currentWeek?.timesheet ? formatWeekRange(currentWeek.timesheet.week_start) : ''}
              </span>
            </div>

            {/* Project Breakdown */}
            {currentWeek?.project_breakdown?.length > 0 && (
              <div className="space-y-2">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Hours by Project</span>
                {currentWeek.project_breakdown.map(project => (
                  <div key={project.id} className="flex items-center justify-between p-2 pl-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: project.color }} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{project.name}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{formatHours(project.hours)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-500/20">
              <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">Total Hours</span>
              <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
                {formatHours(currentWeek?.timesheet?.total_hours)}
              </span>
            </div>

            {/* Note field */}
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">
                Add a note for your manager (optional)
              </label>
              <Input
                placeholder="e.g., Worked overtime on urgent project"
                value={submitNote}
                onChange={(e) => setSubmitNote(e.target.value)}
                className="h-10"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="btn-premium"
              data-testid="confirm-submit"
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Confirm Submit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Approvals;
